import { collection, doc, addDoc, updateDoc, serverTimestamp, query, where, getDocs, orderBy, limit as firestoreLimit } from 'firebase/firestore';
import { db } from '../firebase';

export interface ErrorLog {
  id: string;
  integrationId: string;
  errorType: 'API_ERROR' | 'AUTH_ERROR' | 'SYNC_ERROR' | 'WEBHOOK_ERROR' | 'VALIDATION_ERROR' | 'NETWORK_ERROR';
  errorCode: string;
  errorMessage: string;
  stackTrace?: string;
  context: any;
  timestamp: Date;
  resolved: boolean;
  retryCount: number;
  maxRetries: number;
  nextRetryAt?: Date;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
}

export interface RetryPolicy {
  maxRetries: number;
  baseDelay: number; // milliseconds
  maxDelay: number; // milliseconds
  backoffMultiplier: number;
  jitter: boolean;
}

export interface ErrorMetrics {
  totalErrors: number;
  errorsByType: Record<string, number>;
  errorsByIntegration: Record<string, number>;
  errorsBySeverity: Record<string, number>;
  resolutionRate: number;
  averageResolutionTime: number;
  criticalErrors: number;
  unresolvedErrors: number;
}

class ErrorHandlingService {
  private retryPolicies: Map<string, RetryPolicy> = new Map();
  private errorQueue: Map<string, ErrorLog> = new Map();
  private processingInterval: NodeJS.Timeout | null = null;

  constructor() {
    this.initializeRetryPolicies();
    this.startErrorProcessor();
  }

  // Initialize retry policies for different error types
  private initializeRetryPolicies(): void {
    this.retryPolicies.set('API_ERROR', {
      maxRetries: 5,
      baseDelay: 1000,
      maxDelay: 30000,
      backoffMultiplier: 2,
      jitter: true
    });

    this.retryPolicies.set('AUTH_ERROR', {
      maxRetries: 3,
      baseDelay: 5000,
      maxDelay: 60000,
      backoffMultiplier: 2,
      jitter: true
    });

    this.retryPolicies.set('SYNC_ERROR', {
      maxRetries: 3,
      baseDelay: 2000,
      maxDelay: 20000,
      backoffMultiplier: 1.5,
      jitter: true
    });

    this.retryPolicies.set('WEBHOOK_ERROR', {
      maxRetries: 5,
      baseDelay: 1000,
      maxDelay: 10000,
      backoffMultiplier: 2,
      jitter: true
    });

    this.retryPolicies.set('NETWORK_ERROR', {
      maxRetries: 10,
      baseDelay: 500,
      maxDelay: 30000,
      backoffMultiplier: 2,
      jitter: true
    });

    this.retryPolicies.set('VALIDATION_ERROR', {
      maxRetries: 1,
      baseDelay: 0,
      maxDelay: 0,
      backoffMultiplier: 1,
      jitter: false
    });
  }

  // Start the error processing loop
  private startErrorProcessor(): void {
    if (this.processingInterval) {
      clearInterval(this.processingInterval);
    }

    this.processingInterval = setInterval(async () => {
      await this.processErrorQueue();
    }, 5000); // Process every 5 seconds
  }

  // Stop the error processor
  public stopErrorProcessor(): void {
    if (this.processingInterval) {
      clearInterval(this.processingInterval);
      this.processingInterval = null;
    }
  }

  // Log an error
  public async logError(
    integrationId: string,
    errorType: ErrorLog['errorType'],
    error: Error | string,
    context: any = {},
    severity: ErrorLog['severity'] = 'MEDIUM'
  ): Promise<string> {
    try {
      const errorMessage = error instanceof Error ? error.message : error;
      const stackTrace = error instanceof Error ? error.stack : undefined;
      
      const errorLog: Omit<ErrorLog, 'id'> = {
        integrationId,
        errorType,
        errorCode: this.generateErrorCode(errorType, errorMessage),
        errorMessage,
        stackTrace,
        context,
        timestamp: new Date(),
        resolved: false,
        retryCount: 0,
        maxRetries: this.retryPolicies.get(errorType)?.maxRetries || 3,
        severity
      };

      const docRef = await addDoc(collection(db, 'errorLogs'), {
        ...errorLog,
        timestamp: serverTimestamp()
      });

      const errorId = docRef.id;
      const fullErrorLog: ErrorLog = {
        id: errorId,
        ...errorLog
      };

      // Add to retry queue if retryable
      if (this.isRetryableError(errorType)) {
        this.scheduleRetry(fullErrorLog);
      }

      // Send alert for critical errors
      if (severity === 'CRITICAL') {
        await this.sendCriticalErrorAlert(fullErrorLog);
      }

      return errorId;
    } catch (logError) {
      console.error('Failed to log error:', logError);
      throw logError;
    }
  }

  // Process the error queue
  private async processErrorQueue(): Promise<void> {
    const errorsToProcess = Array.from(this.errorQueue.values());
    
    for (const error of errorsToProcess) {
      if (error.nextRetryAt && error.nextRetryAt > new Date()) {
        continue; // Not time to retry yet
      }

      try {
        await this.retryError(error);
        this.errorQueue.delete(error.id);
      } catch (retryError) {
        console.error(`Error retry failed for ${error.id}:`, retryError);
        
        // Increment retry count
        error.retryCount++;
        
        if (error.retryCount >= error.maxRetries) {
          // Max retries reached, mark as unresolved
          this.errorQueue.delete(error.id);
          await this.markErrorAsUnresolved(error);
        } else {
          // Schedule next retry
          this.scheduleRetry(error);
        }
      }
    }
  }

  // Retry an error
  private async retryError(error: ErrorLog): Promise<void> {
    try {
      // This would contain the actual retry logic based on error type
      // For now, we'll simulate a retry attempt
      console.log(`Retrying error ${error.id} (attempt ${error.retryCount + 1})`);
      
      // Simulate retry logic based on error type
      switch (error.errorType) {
        case 'API_ERROR':
          await this.retryApiCall(error);
          break;
        case 'AUTH_ERROR':
          await this.retryAuth(error);
          break;
        case 'SYNC_ERROR':
          await this.retrySync(error);
          break;
        case 'WEBHOOK_ERROR':
          await this.retryWebhook(error);
          break;
        case 'NETWORK_ERROR':
          await this.retryNetworkOperation(error);
          break;
        default:
          throw new Error(`Unknown error type: ${error.errorType}`);
      }

      // Mark as resolved if retry succeeded
      await this.markErrorAsResolved(error);
    } catch (retryError) {
      throw retryError;
    }
  }

  // Retry API call
  private async retryApiCall(error: ErrorLog): Promise<void> {
    // Implement API retry logic
    console.log('Retrying API call for error:', error.id);
    
    // Simulate API retry
    const success = Math.random() > 0.3; // 70% success rate for demo
    if (!success) {
      throw new Error('API retry failed');
    }
  }

  // Retry authentication
  private async retryAuth(error: ErrorLog): Promise<void> {
    // Implement auth retry logic (e.g., refresh token)
    console.log('Retrying authentication for error:', error.id);
    
    // Simulate auth retry
    const success = Math.random() > 0.2; // 80% success rate for demo
    if (!success) {
      throw new Error('Auth retry failed');
    }
  }

  // Retry sync operation
  private async retrySync(error: ErrorLog): Promise<void> {
    // Implement sync retry logic
    console.log('Retrying sync operation for error:', error.id);
    
    // Simulate sync retry
    const success = Math.random() > 0.4; // 60% success rate for demo
    if (!success) {
      throw new Error('Sync retry failed');
    }
  }

  // Retry webhook processing
  private async retryWebhook(error: ErrorLog): Promise<void> {
    // Implement webhook retry logic
    console.log('Retrying webhook processing for error:', error.id);
    
    // Simulate webhook retry
    const success = Math.random() > 0.3; // 70% success rate for demo
    if (!success) {
      throw new Error('Webhook retry failed');
    }
  }

  // Retry network operation
  private async retryNetworkOperation(error: ErrorLog): Promise<void> {
    // Implement network retry logic
    console.log('Retrying network operation for error:', error.id);
    
    // Simulate network retry
    const success = Math.random() > 0.2; // 80% success rate for demo
    if (!success) {
      throw new Error('Network retry failed');
    }
  }

  // Schedule retry for an error
  private scheduleRetry(error: ErrorLog): void {
    const policy = this.retryPolicies.get(error.errorType);
    if (!policy) {
      return;
    }

    const delay = this.calculateRetryDelay(error.retryCount, policy);
    const nextRetryAt = new Date(Date.now() + delay);
    
    error.nextRetryAt = nextRetryAt;
    this.errorQueue.set(error.id, error);
  }

  // Calculate retry delay with exponential backoff
  private calculateRetryDelay(retryCount: number, policy: RetryPolicy): number {
    let delay = policy.baseDelay * Math.pow(policy.backoffMultiplier, retryCount);
    
    // Cap at max delay
    delay = Math.min(delay, policy.maxDelay);
    
    // Add jitter if enabled
    if (policy.jitter) {
      delay = delay * (0.5 + Math.random() * 0.5);
    }
    
    return Math.floor(delay);
  }

  // Check if error is retryable
  private isRetryableError(errorType: ErrorLog['errorType']): boolean {
    const nonRetryableTypes = ['VALIDATION_ERROR'];
    return !nonRetryableTypes.includes(errorType);
  }

  // Generate error code
  private generateErrorCode(errorType: ErrorLog['errorType'], errorMessage: string): string {
    const typePrefix = errorType.substring(0, 3).toUpperCase();
    const messageHash = this.hashString(errorMessage).toString(16).substring(0, 4);
    return `${typePrefix}-${messageHash}`;
  }

  // Hash string for error code generation
  private hashString(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash);
  }

  // Mark error as resolved
  private async markErrorAsResolved(error: ErrorLog): Promise<void> {
    try {
      await updateDoc(doc(db, 'errorLogs', error.id), {
        resolved: true,
        resolvedAt: serverTimestamp(),
        retryCount: error.retryCount + 1
      });
    } catch (updateError) {
      console.error('Failed to mark error as resolved:', updateError);
    }
  }

  // Mark error as unresolved
  private async markErrorAsUnresolved(error: ErrorLog): Promise<void> {
    try {
      await updateDoc(doc(db, 'errorLogs', error.id), {
        resolved: false,
        unresolvedAt: serverTimestamp(),
        retryCount: error.retryCount
      });
    } catch (updateError) {
      console.error('Failed to mark error as unresolved:', updateError);
    }
  }

  // Send critical error alert
  private async sendCriticalErrorAlert(error: ErrorLog): Promise<void> {
    try {
      // This would integrate with notification services (email, Slack, etc.)
      console.log('CRITICAL ERROR ALERT:', {
        integrationId: error.integrationId,
        errorType: error.errorType,
        errorMessage: error.errorMessage,
        timestamp: error.timestamp
      });

      // Store alert in database
      await addDoc(collection(db, 'errorAlerts'), {
        errorId: error.id,
        integrationId: error.integrationId,
        severity: error.severity,
        message: `Critical error in integration ${error.integrationId}: ${error.errorMessage}`,
        timestamp: serverTimestamp(),
        acknowledged: false
      });
    } catch (alertError) {
      console.error('Failed to send critical error alert:', alertError);
    }
  }

  // Get error metrics
  public async getErrorMetrics(integrationId?: string, timeRange?: { start: Date; end: Date }): Promise<ErrorMetrics> {
    try {
      let errorQuery = query(collection(db, 'errorLogs'));
      
      if (integrationId) {
        errorQuery = query(errorQuery, where('integrationId', '==', integrationId));
      }
      
      if (timeRange) {
        errorQuery = query(
          errorQuery,
          where('timestamp', '>=', timeRange.start),
          where('timestamp', '<=', timeRange.end)
        );
      }

      const errorDocs = await getDocs(errorQuery);
      
      const metrics: ErrorMetrics = {
        totalErrors: 0,
        errorsByType: {},
        errorsByIntegration: {},
        errorsBySeverity: {},
        resolutionRate: 0,
        averageResolutionTime: 0,
        criticalErrors: 0,
        unresolvedErrors: 0
      };

      let resolvedCount = 0;
      let totalResolutionTime = 0;
      let resolutionCount = 0;

      errorDocs.forEach(doc => {
        const data = doc.data();
        metrics.totalErrors++;
        
        // Count by type
        const errorType = data.errorType || 'unknown';
        metrics.errorsByType[errorType] = (metrics.errorsByType[errorType] || 0) + 1;
        
        // Count by integration
        const integration = data.integrationId || 'unknown';
        metrics.errorsByIntegration[integration] = (metrics.errorsByIntegration[integration] || 0) + 1;
        
        // Count by severity
        const severity = data.severity || 'unknown';
        metrics.errorsBySeverity[severity] = (metrics.errorsBySeverity[severity] || 0) + 1;
        
        // Count critical errors
        if (severity === 'CRITICAL') {
          metrics.criticalErrors++;
        }
        
        // Count resolved/unresolved
        if (data.resolved) {
          resolvedCount++;
          
          // Calculate resolution time
          if (data.resolvedAt && data.timestamp) {
            const resolutionTime = data.resolvedAt.toDate().getTime() - data.timestamp.toDate().getTime();
            totalResolutionTime += resolutionTime;
            resolutionCount++;
          }
        } else {
          metrics.unresolvedErrors++;
        }
      });

      // Calculate rates
      metrics.resolutionRate = metrics.totalErrors > 0 ? (resolvedCount / metrics.totalErrors) * 100 : 0;
      metrics.averageResolutionTime = resolutionCount > 0 ? totalResolutionTime / resolutionCount : 0;

      return metrics;
    } catch (error) {
      console.error('Error getting error metrics:', error);
      throw error;
    }
  }

  // Get recent errors
  public async getRecentErrors(limit: number = 50): Promise<ErrorLog[]> {
    try {
      const errorQuery = query(
        collection(db, 'errorLogs'),
        orderBy('timestamp', 'desc'),
        firestoreLimit(limit)
      );
      
      const errorDocs = await getDocs(errorQuery);
      
      return errorDocs.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ErrorLog[];
    } catch (error) {
      console.error('Error getting recent errors:', error);
      throw error;
    }
  }

  // Acknowledge critical error alert
  public async acknowledgeErrorAlert(alertId: string): Promise<void> {
    try {
      await updateDoc(doc(db, 'errorAlerts', alertId), {
        acknowledged: true,
        acknowledgedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error acknowledging alert:', error);
      throw error;
    }
  }

  // Get error alerts
  public async getErrorAlerts(acknowledged: boolean = false): Promise<any[]> {
    try {
      const alertQuery = query(
        collection(db, 'errorAlerts'),
        where('acknowledged', '==', acknowledged),
        orderBy('timestamp', 'desc')
      );
      
      const alertDocs = await getDocs(alertQuery);
      
      return alertDocs.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting error alerts:', error);
      throw error;
    }
  }
}

export const errorHandlingService = new ErrorHandlingService();
